import geo1 from "./geo1.json";
import geo2 from "./geo2.json";
import geo3 from "./geo3.json";
import geo4 from "./geo4.json";
import { hubs } from "./hubs";

export const LANGS = ["en", "it"];
export const DEFAULT_LANG = "en";

export const SLUGS = {
  carRental: { en: "car-rental", it: "noleggio-auto" },
  airports: { en: "airports", it: "aeroporti" },
  deals: { en: "deals", it: "offerte" },
  help: { en: "help", it: "assistenza" },
  about: { en: "about", it: "chi-siamo" },
  contact: { en: "contact", it: "contatti" },
  privacy: { en: "privacy-policy", it: "informativa-privacy" },
  cookies: { en: "cookie-policy", it: "cookie-policy" },
  terms: { en: "terms-and-conditions", it: "termini-e-condizioni" },
  affiliate: { en: "affiliate-disclosure", it: "divulgazione-affiliazione" },
};

export const countries = { ...geo1.countries, ...geo2.countries };
export const pages = [...geo1.pages, ...geo2.pages, ...geo3.pages, ...geo4.pages];

export const cities = pages.filter((p) => p.type === "city");
export const airports = pages.filter((p) => p.type === "airport");

export const byId = Object.fromEntries(pages.map((p) => [p.id, p]));

export { hubs };

export const AFFILIATE_URL = "https://www.discovercars.com/?a_aid=boingcars&currency=eur";

export const siteUrl = (path = "") => `https://www.boingcars.com${path}`;

export function staticPath(lang, key) {
  return `/${lang}/${SLUGS[key][lang]}`;
}

// Flat destination URLs: /en/car-rental/rome, /it/noleggio-auto/roma-fiumicino
export function pagePath(lang, page) {
  return `/${lang}/${SLUGS.carRental[lang]}/${page.slug[lang]}`;
}

export function hubPath(lang, hub) {
  return `/${lang}/${SLUGS.carRental[lang]}/${hub.slugs[lang]}`;
}

export function hubPages(hub) {
  if (hub.pageIds) return hub.pageIds.map((id) => byId[id]).filter(Boolean);
  return pages.filter((p) => p.country === hub.country);
}

export function resolveHub(lang, slug) {
  return Object.values(hubs).find((h) => h.slugs[lang] === slug) || null;
}

export function resolvePage(lang, slug) {
  return pages.find((p) => p.slug[lang] === slug) || null;
}

// Legacy nested URLs (/en/car-rental/italy/rome[/fiumicino-airport]) → flat page
export function resolveLegacy(lang, countrySlug, citySlug, airportSlug) {
  const countryKey = Object.keys(countries).find((k) => countries[k].slug[lang] === countrySlug);
  if (!countryKey) return null;
  const city = pages.find(
    (p) => p.type === "city" && p.country === countryKey && p.slug[lang] === citySlug
  );
  if (!city) return null;
  if (!airportSlug) return city;
  return (
    pages.find(
      (p) =>
        p.type === "airport" &&
        p.parent === city.id &&
        (p.slug[lang] === airportSlug || (p.legacy && p.legacy[lang] === airportSlug))
    ) || null
  );
}

// DiscoverCars channel code for the current URL (GA4 + affiliate channel reporting)
export function channelForPath(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  const lang = segs[0];
  if (lang !== "en" && lang !== "it") return "home";
  if (segs.length === 1) return "home";
  if (segs[1] === SLUGS.carRental[lang] && segs.length >= 3) {
    const hub = resolveHub(lang, segs[2]);
    if (hub) return hub.chan;
    const page = resolvePage(lang, segs[2]);
    if (page) return page.chan || "site";
  }
  return staticSlugToKey(lang, segs[1]) || "site";
}

export function relatedPages(page, lang) {
  const rel = [];
  if (page.type === "city") {
    rel.push(...pages.filter((p) => p.type === "airport" && p.parent === page.id).slice(0, 2));
  } else if (page.parent) {
    rel.push(byId[page.parent]);
    rel.push(...pages.filter((p) => p.type === "airport" && p.parent === page.parent && p.id !== page.id));
  }
  if (page.nearby) rel.push(...page.nearby.map((id) => byId[id]).filter(Boolean));

  const seen = new Set([page.id]);
  const out = [];
  for (const p of rel) {
    if (p && !seen.has(p.id)) {
      seen.add(p.id);
      out.push(p);
    }
  }
  if (out.length < 4) {
    const pool = pages.filter((p) => !seen.has(p.id));
    const sameCountry = pool.filter((p) => p.country === page.country);
    const rest = pool.filter((p) => p.country !== page.country);
    for (const p of [...sameCountry, ...rest]) {
      if (out.length >= 4) break;
      seen.add(p.id);
      out.push(p);
    }
  }
  return out.slice(0, 4);
}

export function staticSlugToKey(lang, slug) {
  for (const [key, val] of Object.entries(SLUGS)) {
    if (val[lang] === slug) return key;
  }
  return null;
}
